import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/client';
import StatusBadge from '../components/shared/StatusBadge';
import EmptyState from '../components/shared/EmptyState';
import { Ticket, Search, Filter, X, Zap, Tag } from 'lucide-react';
import { cn } from '../utils/cn';
import { tagLabel } from '../utils/tagLabel';

const STATUS_VALUES = ['new', 'in_progress', 'waiting_customer', 'info_requested', 'resolved', 'escalated'];
const INTENT_VALUES = ['activation_problem', 'installation_help', 'download_access', 'order_not_received', 'refund_request', 'other'];

const TAG_COLORS = {
  from_imap: 'bg-blue-100 text-blue-700',
  autopilot_sent: 'bg-purple-100 text-purple-700',
  replacement_key_sent: 'bg-green-100 text-green-700',
  escalated: 'bg-red-100 text-red-700',
  urgent: 'bg-red-100 text-red-700',
  vip: 'bg-yellow-100 text-yellow-700',
  manually_resolved: 'bg-green-100 text-green-700',
};

export default function Tickets() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState({ data: [], total: 0 });
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    intent: searchParams.get('intent') || '',
    shop_id: searchParams.get('shop_id') || '',
    tag: searchParams.get('tag') || '',
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
    archived: searchParams.get('archived') || '',
    page: parseInt(searchParams.get('page') || '1'),
    per_page: parseInt(searchParams.get('per_page') || localStorage.getItem('tickets_per_page') || '25'),
  });

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.intent) params.intent = filters.intent;
      if (filters.shop_id) params.shop_id = filters.shop_id;
      if (filters.tag) params.tag = filters.tag;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.archived) params.archived = 1;
      params.page = filters.page;
      params.per_page = filters.per_page;

      const [ticketsRes, shopsRes] = await Promise.all([
        api.get('/tickets', { params }),
        api.get('/shops'),
      ]);

      setTickets(ticketsRes.data);
      setShops(shopsRes.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);

    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => { if (v && v !== '1') params.set(k, v); });
    if (newFilters.archived) params.set('archived', '1');
    setSearchParams(params);
  };

  const setView = (view) => {
    const archived = view === 'archive' ? '1' : '';
    const newFilters = { ...filters, archived, page: 1 };
    setFilters(newFilters);

    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => { if (k !== 'archived' && v && v !== '1') params.set(k, v); });
    if (archived) params.set('archived', '1');
    setSearchParams(params);
  };

  const clearFilters = () => {
    const cleared = { search: '', status: '', intent: '', shop_id: '', tag: '', date_from: '', date_to: '', archived: filters.archived, page: 1, per_page: filters.per_page };
    setFilters(cleared);
    const params = new URLSearchParams();
    if (cleared.archived) params.set('archived', '1');
    setSearchParams(params);
  };

  const updatePerPage = (value) => {
    const perPage = parseInt(value);
    localStorage.setItem('tickets_per_page', String(perPage));
    const newFilters = { ...filters, per_page: perPage, page: 1 };
    setFilters(newFilters);
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => { if (v && v !== '1' && !(k === 'per_page' && v === 25)) params.set(k, v); });
    if (newFilters.archived) params.set('archived', '1');
    setSearchParams(params);
  };

  const view = filters.archived ? 'archive' : 'inbox';

  const hasActiveFilters = filters.status || filters.intent || filters.shop_id || filters.tag || filters.date_from || filters.date_to;

  const ticketList = tickets.data || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('tickets.title')}</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{tickets.total || 0} {t('common.total')}</span>
          <label className="flex items-center gap-2 text-sm text-gray-500">
            <span>{t('common.per_page')}</span>
            <select
              value={filters.per_page}
              onChange={(e) => updatePerPage(e.target.value)}
              className="rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500 py-1"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </label>
        </div>
      </div>

      {/* Inbox / Archive tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
        {[{ key: 'inbox', label: t('tickets.inbox') }, { key: 'archive', label: t('tickets.archive') }].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setView(tab.key)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              view === tab.key
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search and filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4">
        <div className="flex items-center gap-3 px-4 py-3">
          <Search className="h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            placeholder={t('tickets.search_placeholder')}
            className="flex-1 border-0 bg-transparent focus:ring-0 text-sm placeholder:text-gray-400"
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
              hasActiveFilters
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            )}
          >
            <Filter className="h-4 w-4" />
            {t('common.filters')}
            {hasActiveFilters && (
              <span className="bg-primary-600 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">
                {[filters.status, filters.intent, filters.shop_id, filters.tag, filters.date_from].filter(Boolean).length}
              </span>
            )}
          </button>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <X className="h-4 w-4" /> {t('common.clear')}
            </button>
          )}
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="px-4 py-3 border-t border-gray-200 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.status')}</label>
              <select value={filters.status} onChange={(e) => updateFilter('status', e.target.value)} className="block w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500">
                <option value="">{t('filters.all')}</option>
                {STATUS_VALUES.map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.intent')}</label>
              <select value={filters.intent} onChange={(e) => updateFilter('intent', e.target.value)} className="block w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500">
                <option value="">{t('filters.all')}</option>
                {INTENT_VALUES.map((i) => <option key={i} value={i}>{t(`intent.${i}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.shop')}</label>
              <select value={filters.shop_id} onChange={(e) => updateFilter('shop_id', e.target.value)} className="block w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500">
                <option value="">{t('filters.all')}</option>
                {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.tag')}</label>
              <select value={filters.tag} onChange={(e) => updateFilter('tag', e.target.value)} className="block w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500">
                <option value="">{t('filters.all')}</option>
                <option value="from_imap">From IMAP</option>
                <option value="autopilot_sent">Autopilot Sent</option>
                <option value="replacement_key_sent">Key Replaced</option>
                <option value="needs_human">Needs Human</option>
                <option value="urgent">Urgent</option>
                <option value="vip">VIP</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.date_from')}</label>
              <input type="date" value={filters.date_from} onChange={(e) => updateFilter('date_from', e.target.value)} className="block w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">{t('filters.date_to')}</label>
              <input type="date" value={filters.date_to} onChange={(e) => updateFilter('date_to', e.target.value)} className="block w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500" />
            </div>
          </div>
        )}
      </div>

      {/* Status quick filters */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
        {STATUS_VALUES.map((s) => (
          <button
            key={s}
            onClick={() => updateFilter('status', filters.status === s ? '' : s)}
            className={cn(
              'px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors',
              filters.status === s
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            )}
          >
            {t(`status.${s}`)}
          </button>
        ))}
      </div>

      {/* Ticket list */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
      ) : ticketList.length === 0 ? (
        <EmptyState icon={Ticket} title={t('tickets.no_results')} description={hasActiveFilters ? t('tickets.no_results_hint') : ''} />
      ) : (
        <div className="space-y-2">
          {ticketList.map((ticket) => (
            <Link
              key={ticket.id}
              to={`/tickets/${ticket.id}`}
              className={cn(
                'block bg-white rounded-xl border shadow-sm hover:shadow-md transition-all p-4',
                ticket.has_unread_customer_message
                  ? 'border-l-4 border-l-blue-500 border-y-gray-200 border-r-gray-200 hover:border-l-blue-600'
                  : 'border-gray-200 hover:border-primary-300'
              )}
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {ticket.has_unread_customer_message && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 flex-shrink-0">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                        {t('tags.new_badge')}
                      </span>
                    )}
                    <h3 className={cn(
                      'text-sm truncate',
                      ticket.has_unread_customer_message ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'
                    )}>{ticket.subject}</h3>
                    {ticket.tags?.includes('autopilot_sent') && (
                      <Zap className="h-4 w-4 text-purple-500 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{ticket.customer_email}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <StatusBadge status={ticket.status} />
                    {ticket.intent && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        {t(`intent.${ticket.intent}`, { defaultValue: ticket.intent.replace(/_/g, ' ') })}
                      </span>
                    )}
                    {ticket.tags?.map((tag) => (
                      <span
                        key={tag}
                        className={cn(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                          TAG_COLORS[tag] || 'bg-gray-100 text-gray-600'
                        )}
                      >
                        <Tag className="h-3 w-3" />
                        {tagLabel(tag, t)}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-gray-400">{new Date(ticket.created_at).toLocaleDateString()}</p>
                  <p className="text-xs text-gray-400">{new Date(ticket.created_at).toLocaleTimeString()}</p>
                  {ticket.shop && (
                    <p className="text-xs text-gray-500 mt-1">{ticket.shop.name}</p>
                  )}
                </div>
              </div>
            </Link>
          ))}

          {/* Pagination */}
          {tickets.last_page > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              {Array.from({ length: tickets.last_page }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => updateFilter('page', page)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                    page === tickets.current_page
                      ? 'bg-primary-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                  )}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
